import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';
import { SiOpenai, SiGithubcopilot, SiClaude } from 'react-icons/si';
import { TbRobot } from 'react-icons/tb';

const RED = '#C03626';
const EMPTY_FORM = {
  name: '', changeDescription: '', description: '', instructions: '',
  conversationStarters: '', knowledgeLink: '',
  platforms: { gpt: false, claude: false, copilot: false },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const latestVersion = (a) => a.versions?.[a.versions.length - 1] || {};
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('da-DK', { day: 'numeric', month: 'short' })
  : '';

const PlatformIcon = ({ platform, active }) => {
  const color = active ? RED : '#ccc';
  const size = '1.1rem';
  if (platform === 'gpt')     return <SiOpenai       title="ChatGPT" style={{ fontSize: size, color }} />;
  if (platform === 'claude')  return <SiClaude       title="Claude"  style={{ fontSize: size, color }} />;
  if (platform === 'copilot') return <SiGithubcopilot title="Copilot" style={{ fontSize: size, color }} />;
  return null;
};

// ── Toggle switch ─────────────────────────────────────────────────────────────
const Toggle = ({ label, icon: Icon, checked, onChange, readOnly }) => (
  <div style={toggleRowStyle}>
    <Icon style={{ fontSize: '1.4rem', color: checked ? RED : '#aaa', transition: 'color 0.2s' }} />
    <span style={{ fontWeight: 600, color: '#2A0D05', fontSize: '0.95rem', flex: 1 }}>{label}</span>
    <div
      onClick={() => !readOnly && onChange(!checked)}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: checked ? RED : '#ddd',
        position: 'relative',
        cursor: readOnly ? 'default' : 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
        opacity: readOnly ? 0.7 : 1,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: checked ? 23 : 3,
        width: 18, height: 18, borderRadius: '50%',
        background: '#fff', transition: 'left 0.2s',
        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
      }} />
    </div>
  </div>
);

// ── Version tab bar ───────────────────────────────────────────────────────────
const TabBar = ({ count, activeIdx, isNewActive, dates, onTab, onAdd }) => (
  <div style={tabBarStyle}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <button
          onClick={() => onTab(i)}
          style={{ ...tabStyle, ...(!isNewActive && activeIdx === i ? tabActiveStyle : {}) }}
        >
          v{i + 1}
        </button>
        {dates?.[i] && <span style={tabDateStyle}>{fmtDate(dates[i])}</span>}
      </div>
    ))}
    {isNewActive && (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <button style={{ ...tabStyle, ...tabActiveStyle }}>v{count + 1}</button>
        <span style={tabDateStyle}>ny</span>
      </div>
    )}
    {/* Hide "+" while already adding a new version */}
    {!isNewActive && (
      <button onClick={onAdd} style={tabAddStyle} title="Tilføj version">+</button>
    )}
  </div>
);

// ── Modal ─────────────────────────────────────────────────────────────────────
const Modal = ({ mode, initial, onClose, onSave, onAddVersion, onDelete, onEditVersion }) => {
  const isView = mode === 'view';

  const [tabs, setTabs] = useState(
    isView
      ? (initial?.versions?.length ? initial.versions : [{ ...EMPTY_FORM }])
      : [{ ...EMPTY_FORM }]
  );
  const [activeTab, setActiveTab] = useState(0);
  const [isNewTab, setIsNewTab]   = useState(false);
  const [newForm, setNewForm]     = useState({ ...EMPTY_FORM });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting]     = useState(false);

  // Active form data and read-only flag
  const activeForm = isNewTab ? newForm : isEditMode ? editForm : tabs[activeTab];
  const isReadOnly = isView && !isNewTab && !isEditMode;

  const updateForm = (updater) => {
    const fn = typeof updater === 'function' ? updater : () => updater;
    if (isNewTab)        setNewForm(fn);
    else if (isEditMode) setEditForm(fn);
    else if (!isView)    setTabs((t) => t.map((v, i) => i === activeTab ? fn(v) : v));
  };

  const set         = (key, val) => updateForm((f) => ({ ...f, [key]: val }));
  const setPlatform = (key, val) => updateForm((f) => ({ ...f, platforms: { ...f.platforms, [key]: val } }));

  const handleAddTab = () => {
    if (isView) {
      setIsNewTab(true);
      setIsEditMode(false);
      setEditForm(null);
      setNewForm({ ...EMPTY_FORM });
    } else {
      setTabs((t) => [...t, { ...EMPTY_FORM }]);
      setActiveTab(tabs.length);
    }
  };

  const handleTabClick = (i) => {
    if (isNewTab) setIsNewTab(false);
    if (isEditMode) { setIsEditMode(false); setEditForm(null); }
    setError('');
    setActiveTab(i);
  };

  // Save new assistant (add mode)
  const handleSave = async () => {
    setError('');
    if (isView) {
      if (!newForm.name.trim()) { setError('Navn er påkrævet.'); return; }
      setSaving(true);
      try {
        const updated = await onAddVersion(initial._id, newForm);
        setTabs(updated.versions);
        setIsNewTab(false);
        setActiveTab(updated.versions.length - 1);
      } catch {
        setError('Noget gik galt. Prøv igen.');
      } finally { setSaving(false); }
    } else {
      if (tabs.some((v) => !v.name.trim())) { setError('Alle versioner skal have et navn.'); return; }
      setSaving(true);
      try {
        await onSave(tabs);
      } catch {
        setError('Noget gik galt. Prøv igen.');
      } finally { setSaving(false); }
    }
  };

  // Save edits to an existing version
  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) { setError('Navn er påkrævet.'); return; }
    setSaving(true);
    setError('');
    try {
      const updated = await onEditVersion(initial._id, activeTab, editForm);
      setTabs(updated.versions);
      setIsEditMode(false);
      setEditForm(null);
    } catch {
      setError('Noget gik galt. Prøv igen.');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete(initial._id);
      onClose();
    } catch {
      setError('Sletning mislykkedes. Prøv igen.');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>

        {/* Tab bar */}
        <TabBar
          count={tabs.length}
          activeIdx={activeTab}
          isNewActive={isNewTab}
          dates={isView ? tabs.map((v) => v.createdAt) : undefined}
          onTab={handleTabClick}
          onAdd={handleAddTab}
        />

        <h2 style={{ margin: '1rem 0', color: '#2A0D05', fontSize: '1.2rem' }}>
          {isEditMode
            ? `Rediger — v${activeTab + 1}`
            : isNewTab
              ? `Ny version (v${tabs.length + 1})`
              : isView
                ? `${activeForm.name || '—'} — v${activeTab + 1}`
                : `Ny assistent — v${activeTab + 1}`}
        </h2>

        {/* Change description — only for v2+ */}
        {(isNewTab || activeTab > 0) && (
          <div style={{ marginBottom: '1.1rem' }}>
            <label style={fieldLabelStyle}>Hvad er ændret?</label>
            <textarea
              value={activeForm.changeDescription || ''}
              onChange={(e) => set('changeDescription', e.target.value)}
              readOnly={isReadOnly}
              placeholder={isReadOnly ? '' : 'Beskriv kort hvad der er nyt i denne version…'}
              rows={3}
              style={{ ...inputStyle, resize: 'vertical', height: 'auto' }}
            />
          </div>
        )}

        {/* Platform toggles */}
        <div style={{ marginBottom: '1.1rem' }}>
          <label style={fieldLabelStyle}>Platform</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.4rem' }}>
            <Toggle label="ChatGPT" icon={SiOpenai}        checked={activeForm.platforms?.gpt     || false} readOnly={isReadOnly} onChange={(v) => setPlatform('gpt', v)} />
            <Toggle label="Claude"  icon={SiClaude}        checked={activeForm.platforms?.claude   || false} readOnly={isReadOnly} onChange={(v) => setPlatform('claude', v)} />
            <Toggle label="Copilot" icon={SiGithubcopilot} checked={activeForm.platforms?.copilot  || false} readOnly={isReadOnly} onChange={(v) => setPlatform('copilot', v)} />
          </div>
        </div>

        {/* Text fields */}
        {[
          { key: 'name',                 label: 'Navn',                    multiline: false },
          { key: 'description',          label: 'Beskrivelse',             multiline: true  },
          { key: 'instructions',         label: 'Instruktioner',           multiline: true  },
          { key: 'conversationStarters', label: 'Samtale-starters',        multiline: true  },
        ].map(({ key, label, multiline }) => (
          <div key={key} style={{ marginBottom: '0.85rem' }}>
            <label style={fieldLabelStyle}>{label}</label>
            {multiline ? (
              <textarea
                value={activeForm[key] || ''}
                onChange={(e) => set(key, e.target.value)}
                readOnly={isReadOnly}
                rows={3}
                style={{ ...inputStyle, resize: 'vertical', height: 'auto' }}
              />
            ) : (
              <input
                type="text"
                value={activeForm[key] || ''}
                onChange={(e) => set(key, e.target.value)}
                readOnly={isReadOnly}
                style={inputStyle}
              />
            )}
          </div>
        ))}

        {/* SharePoint link — clickable in view mode */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={fieldLabelStyle}>Viden (SharePoint link)</label>
          {isReadOnly && activeForm.knowledgeLink ? (
            <a
              href={activeForm.knowledgeLink}
              target="_blank"
              rel="noopener noreferrer"
              style={linkStyle}
            >
              {activeForm.knowledgeLink}
            </a>
          ) : (
            <input
              type="text"
              value={activeForm.knowledgeLink || ''}
              onChange={(e) => set('knowledgeLink', e.target.value)}
              readOnly={isReadOnly}
              placeholder="https://..."
              style={inputStyle}
            />
          )}
        </div>

        {error && <p style={errorStyle}>{error}</p>}

        {/* Delete confirmation */}
        {isView && confirmDelete && !isEditMode && (
          <div style={confirmBoxStyle}>
            <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: '#2A0D05' }}>
              Er du sikker på, at du vil slette denne assistent?
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(false)} style={btnSecondary}>Annuller</button>
              <button onClick={handleDelete} disabled={deleting} style={btnDelete}>
                {deleting ? 'Sletter…' : 'Ja, slet'}
              </button>
            </div>
          </div>
        )}

        {/* Button row */}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Left: Slet — only in normal view mode */}
          {isView && !isNewTab && !isEditMode && !confirmDelete
            ? <button onClick={() => setConfirmDelete(true)} style={btnDelete}>Slet</button>
            : <div />
          }
          {/* Right: context-dependent action buttons */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {isEditMode ? (
              <>
                <button onClick={() => { setIsEditMode(false); setEditForm(null); setError(''); }} style={btnSecondary}>Annuller</button>
                <button onClick={handleSaveEdit} disabled={saving} style={btnPrimary}>
                  {saving ? 'Gemmer…' : 'Gem ændringer'}
                </button>
              </>
            ) : (isNewTab || !isView) ? (
              <>
                <button onClick={onClose} style={btnSecondary}>Annuller</button>
                <button onClick={handleSave} disabled={saving} style={btnPrimary}>
                  {saving ? 'Gemmer…' : 'Tilføj'}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { setIsEditMode(true); setEditForm({ ...tabs[activeTab] }); setError(''); }} style={btnSecondary}>
                  Rediger
                </button>
                <button onClick={onClose} style={btnSecondary}>Luk</button>
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const AssistentDokumentation = () => {
  const [assistants, setAssistants]   = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showAdd, setShowAdd]         = useState(false);
  const [viewTarget, setViewTarget]   = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [hoveredAdd, setHoveredAdd]   = useState(false);

  useEffect(() => {
    fetch('/api/assistants', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => setAssistants(Array.isArray(data) ? data : []))
      .catch(() => setAssistants([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (versions) => {
    const res = await fetch('/api/assistants', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ versions }),
    });
    if (!res.ok) throw new Error('Server error');
    const created = await res.json();
    setAssistants((prev) => [created, ...prev]);
    setShowAdd(false);
  };

  const handleDelete = async (id) => {
    const res = await fetch(`/api/assistants/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw new Error('Server error');
    setAssistants((prev) => prev.filter((a) => a._id !== id));
  };

  const handleAddVersion = async (id, versionData) => {
    const res = await fetch(`/api/assistants/${id}/versions`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(versionData),
    });
    if (!res.ok) throw new Error('Server error');
    const updated = await res.json();
    setAssistants((prev) => prev.map((a) => a._id === id ? updated : a));
    setViewTarget((prev) => prev?._id === id ? updated : prev);
    return updated;
  };

  const handleEditVersion = async (id, versionIndex, formData) => {
    const res = await fetch(`/api/assistants/${id}/versions/${versionIndex}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });
    if (!res.ok) throw new Error('Server error');
    const updated = await res.json();
    setAssistants((prev) => prev.map((a) => a._id === id ? updated : a));
    setViewTarget((prev) => prev?._id === id ? updated : prev);
    return updated;
  };

  return (
    <div style={pageStyle}>
      <SeoHead
        title="Assistent dokumentation | AICE"
        description="Intern dokumentation for AICE AI-assistenter."
        canonicalPath="/aice/assistenter"
        robots="noindex,nofollow"
      />

      {/* Back navigation */}
      <Link to="/aice" style={backLinkStyle}>← Tilbage til AICE</Link>

      <div style={headerRowStyle}>
        <h1 style={{ margin: 0, color: '#2A0D05', fontSize: '1.6rem' }}>Assistent dokumentation</h1>
        <button
          style={{ ...addBtnStyle, ...(hoveredAdd ? addBtnHoverStyle : {}) }}
          onMouseEnter={() => setHoveredAdd(true)}
          onMouseLeave={() => setHoveredAdd(false)}
          onClick={() => setShowAdd(true)}
        >
          + Tilføj
        </button>
      </div>

      {loading ? (
        <p style={{ color: '#5C3A32' }}>Henter assistenter…</p>
      ) : assistants.length === 0 ? (
        <p style={{ color: '#5C3A32' }}>Ingen assistenter endnu. Tryk "+ Tilføj" for at oprette den første.</p>
      ) : (
        <div style={gridStyle}>
          {assistants.map((a) => {
            const lv = latestVersion(a);
            const vCount = a.versions?.length || 1;
            return (
              <div
                key={a._id}
                style={{ ...cardStyle, ...(hoveredCard === a._id ? cardHoverStyle : {}) }}
                onMouseEnter={() => setHoveredCard(a._id)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={() => setViewTarget(a)}
              >
                <TbRobot style={{ fontSize: '2rem', color: RED, marginBottom: '0.4rem' }} />
                <span style={cardNameStyle}>{lv.name}</span>
                <span style={cardVersionStyle}>v{vCount}</span>
                <div style={platformRowStyle}>
                  <PlatformIcon platform="gpt"     active={lv.platforms?.gpt} />
                  <PlatformIcon platform="claude"  active={lv.platforms?.claude} />
                  <PlatformIcon platform="copilot" active={lv.platforms?.copilot} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <Modal mode="add" onClose={() => setShowAdd(false)} onSave={handleSave} onAddVersion={() => {}} onDelete={() => {}} onEditVersion={() => {}} />
      )}
      {viewTarget && (
        <Modal
          mode="view"
          initial={viewTarget}
          onClose={() => setViewTarget(null)}
          onSave={() => {}}
          onAddVersion={handleAddVersion}
          onDelete={handleDelete}
          onEditVersion={handleEditVersion}
        />
      )}
    </div>
  );
};

export default AssistentDokumentation;

// ── Styles ────────────────────────────────────────────────────────────────────

const pageStyle        = { padding: '2rem', maxWidth: 960, margin: '0 auto' };
const backLinkStyle    = { display: 'inline-block', color: RED, fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none', marginBottom: '1.25rem' };
const headerRowStyle   = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' };
const addBtnStyle      = { background: 'linear-gradient(135deg, #C03626, #FF6F61)', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.55rem 1.1rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' };
const addBtnHoverStyle = { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(192, 54, 38, 0.3)' };
const gridStyle        = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1.25rem' };
const cardStyle        = { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '1.5rem 1rem', border: `2px solid ${RED}`, borderRadius: '16px', background: '#fff', cursor: 'pointer', transition: 'transform 0.2s ease, box-shadow 0.2s ease', textAlign: 'center' };
const cardHoverStyle   = { transform: 'translateY(-5px)', boxShadow: '0 16px 36px rgba(192, 54, 38, 0.2)' };
const cardNameStyle    = { fontWeight: 700, fontSize: '1rem', color: '#2A0D05', marginBottom: '0.15rem' };
const cardVersionStyle = { fontSize: '0.72rem', color: RED, fontWeight: 600, marginBottom: '0.2rem' };
const platformRowStyle = { display: 'flex', gap: '0.5rem', marginTop: '0.4rem' };

const tabBarStyle  = { display: 'flex', alignItems: 'flex-end', gap: '0.35rem', flexWrap: 'wrap', borderBottom: '2px solid #F0D5CF', paddingBottom: '0.5rem', marginBottom: '0' };
const tabStyle     = { padding: '0.3rem 0.75rem', borderRadius: '6px 6px 0 0', border: '1px solid #EBC4BB', background: '#FFF7F5', color: '#2A0D05', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const tabActiveStyle = { background: RED, color: '#fff', border: `1px solid ${RED}` };
const tabAddStyle  = { padding: '0.3rem 0.6rem', borderRadius: '6px', border: `1px dashed ${RED}`, background: 'transparent', color: RED, fontSize: '1rem', fontWeight: 700, cursor: 'pointer', alignSelf: 'center' };
const tabDateStyle = { fontSize: '0.62rem', color: '#999', whiteSpace: 'nowrap' };

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '1rem' };
const modalStyle   = { background: '#fff', borderRadius: '16px', padding: '2rem', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.15)' };

const fieldLabelStyle = { display: 'block', fontWeight: 600, fontSize: '0.88rem', color: '#2A0D05', marginBottom: '0.3rem' };
const inputStyle      = { width: '100%', padding: '0.6rem 0.75rem', borderRadius: '8px', border: '1px solid #EBC4BB', background: '#FFF7F5', fontSize: '0.95rem', color: '#2A0D05', boxSizing: 'border-box', fontFamily: 'inherit' };
const toggleRowStyle  = { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.4rem 0.6rem', borderRadius: '8px', background: '#FFF7F5' };

const btnPrimary      = { background: 'linear-gradient(135deg, #C03626, #FF6F61)', color: '#fff', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' };
const btnSecondary    = { background: 'transparent', color: '#C03626', border: '1px solid #C03626', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' };
const errorStyle      = { color: '#C03626', background: '#FFF0EC', border: '1px solid #F5DAD3', padding: '0.5rem 0.75rem', borderRadius: '8px', fontSize: '0.9rem', marginBottom: '0.75rem' };
const btnDelete       = { background: 'transparent', color: '#b91c1c', border: '1px solid #b91c1c', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' };
const confirmBoxStyle = { background: '#FFF0EC', border: '1px solid #F5DAD3', borderRadius: '10px', padding: '1rem', marginBottom: '1rem' };
const linkStyle       = { display: 'inline-block', color: RED, fontWeight: 600, fontSize: '0.9rem', wordBreak: 'break-all', padding: '0.5rem 0', textDecoration: 'underline' };
